/**
 * Admission Visit Options Panel
 *
 * Replaces manual provider selection with intelligent optimizer-driven options.
 * Displays top 3 pre-scored visit options using the existing visit optimizer.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  FormControl,
  FormControlLabel,
  Grid2,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { addHours, addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useSchedulerOptionsQuery, SchedulerOptionsQuery } from '~/graphql/__generated__/graphql';
import { useOrgConfig } from '~/providers/OrgConfigProvider';
import { timeLabelWithZone } from '@caredna-marlin/utils/dates';
import { VisitType } from '~/providers/OrgConfigProvider';
import { bestOptions } from '~/utils/scheduleOptions';
import { calculateDuration, getServiceDefaults } from '~/utils/visitsHelper';

export interface SelectedAdmissionOption {
  startTime: string;
  resources: Array<{
    providerId: number;
    shiftId: number;
    raceConditionHash: string;
  }>;
  runId: string;
  scores: {
    totalScore: number;
    driveScore: number;
    proximityScore: number;
    utilizationScore: number;
  };
  providerNames: string[]; // Array of provider full names
}

interface AdmissionVisitOptionsPanelProps {
  patientId: number;
  recommendedTime: string | null;
  admissionVisitType: VisitType | null;
  selectedVisitTypeId: number | null;
  onVisitTypeChange: (visitTypeId: number) => void;
  serviceLineId: number;
  onOptionSelected: (option: SelectedAdmissionOption | null) => void;
  disabled?: boolean;
  patientTimezone: string;
}

type ScheduleOption = SchedulerOptionsQuery['scheduleOption']['options'][number];

export function AdmissionVisitOptionsPanel({
  patientId,
  recommendedTime,
  admissionVisitType,
  selectedVisitTypeId,
  onVisitTypeChange,
  serviceLineId,
  onOptionSelected,
  disabled = false,
  patientTimezone,
}: AdmissionVisitOptionsPanelProps) {
  const orgConfig = useOrgConfig();

  const [selectedOption, setSelectedOption] = useState<ScheduleOption | null>(null);

  // Create unique option ID for comparison
  const getOptionId = (option: ScheduleOption) => {
    return `${option.startTime}-${option.resources
      .map((r) => r.providerId)
      .sort()
      .join(',')}`;
  };

  // Get visit type for query
  const visitType = selectedVisitTypeId
    ? orgConfig.visitTypes[selectedVisitTypeId]
    : admissionVisitType;

  // Calculate duration based on visit type and default services
  const serviceIds = useMemo(() => {
    if (!visitType) return [];
    return getServiceDefaults(orgConfig, visitType.id);
  }, [visitType, orgConfig]);

  const duration = useMemo(() => {
    if (!visitType) return 0;
    return parseInt(calculateDuration(orgConfig, visitType.id, serviceIds));
  }, [visitType, orgConfig, serviceIds]);

  // Query window for today: start at recommendedTime, cap at midnight in patient timezone
  const queryEndTime = useMemo(() => {
    if (!recommendedTime || !visitType) return new Date().toISOString();

    // Build midnight of the recommended date correctly in the patient timezone.
    const midnightStr = formatInTimeZone(
      new Date(recommendedTime),
      patientTimezone,
      "yyyy-MM-dd'T'23:59:59xxx"
    );
    const midnightTonight = new Date(midnightStr);

    // Also allow up to 4 hours past the recommended time as a safety floor
    const fourHoursOut = addHours(new Date(recommendedTime), 4);

    return (midnightTonight < fourHoursOut ? midnightTonight : fourHoursOut).toISOString();
  }, [recommendedTime, visitType, patientTimezone]);

  // Query window for tomorrow: 9 AM → midnight in patient timezone
  const tomorrowQueryStartTime = useMemo(() => {
    if (!recommendedTime) return new Date().toISOString();
    return formatInTimeZone(
      addDays(new Date(recommendedTime), 1),
      patientTimezone,
      "yyyy-MM-dd'T'09:00:00xxx"
    );
  }, [recommendedTime, patientTimezone]);

  const tomorrowQueryEndTime = useMemo(() => {
    if (!recommendedTime) return new Date().toISOString();
    return formatInTimeZone(
      addDays(new Date(recommendedTime), 1),
      patientTimezone,
      "yyyy-MM-dd'T'23:59:59xxx"
    );
  }, [recommendedTime, patientTimezone]);

  // Query schedule options from optimizer (today)
  const skipQuery = !recommendedTime || !patientId || !visitType || disabled || duration === 0;

  const { data, loading, error } = useSchedulerOptionsQuery({
    skip: skipQuery,
    variables: {
      patientId,
      startTime: recommendedTime!,
      targetTime: recommendedTime!,
      endTime: queryEndTime,
      serviceLineId,
      organizationalUnitId: null,
      visitTypeId: visitType?.id!,
      duration,
      serviceIds: serviceIds.map((sid) => parseInt(sid)),
      providerTraits: [],
      daysOfWeek: null,
      hoursOfDay: null,
      reschedulingVisitId: null,
      selectedProviderIds: null,
      useOverflowShifts: visitType?.schedulingConfig.allowOverflowShifts || false,
    },
    fetchPolicy: 'cache-and-network',
  });

  const options = useMemo(() => {
    if (!data?.scheduleOption?.options) return [];
    return data.scheduleOption.options;
  }, [data]);

  // Get top 3 unique options strictly between recommendedTime and midnight in patient timezone
  const topThreeOptions = useMemo(() => {
    const recommendedMs = recommendedTime ? new Date(recommendedTime).getTime() : 0;
    const midnightMs = recommendedTime
      ? new Date(
          formatInTimeZone(new Date(recommendedTime), patientTimezone, "yyyy-MM-dd'T'23:59:59xxx")
        ).getTime()
      : Infinity;

    const uniqueOptions = options
      .filter((o) => {
        const t = new Date(o.startTime).getTime();
        return t >= recommendedMs && t <= midnightMs;
      })
      .reduce((acc, option) => {
        const key = getOptionId(option);
        if (!acc.some((o) => getOptionId(o) === key)) {
          acc.push(option);
        }
        return acc;
      }, [] as ScheduleOption[]);

    return bestOptions(uniqueOptions, 3);
  }, [options, recommendedTime]);

  // Tomorrow query — only runs when today has no options
  const skipTomorrowQuery = skipQuery || loading || topThreeOptions.length > 0;

  const { data: tomorrowData, loading: tomorrowLoading, error: tomorrowError } = useSchedulerOptionsQuery({
    skip: skipTomorrowQuery,
    variables: {
      patientId,
      startTime: tomorrowQueryStartTime,
      targetTime: tomorrowQueryStartTime,
      endTime: tomorrowQueryEndTime,
      serviceLineId,
      organizationalUnitId: null,
      visitTypeId: visitType?.id!,
      duration,
      serviceIds: serviceIds.map((sid) => parseInt(sid)),
      providerTraits: [],
      daysOfWeek: null,
      hoursOfDay: null,
      reschedulingVisitId: null,
      selectedProviderIds: null,
      useOverflowShifts: visitType?.schedulingConfig.allowOverflowShifts || false,
    },
    fetchPolicy: 'cache-and-network',
  });

  const tomorrowOptions = useMemo(() => {
    if (!tomorrowData?.scheduleOption?.options) return [];
    const tomorrowStartMs = new Date(tomorrowQueryStartTime).getTime();
    const tomorrowEndMs = new Date(tomorrowQueryEndTime).getTime();
    const unique = tomorrowData.scheduleOption.options
      .filter((o) => {
        const t = new Date(o.startTime).getTime();
        return t >= tomorrowStartMs && t <= tomorrowEndMs;
      })
      .reduce((acc, option) => {
        const key = getOptionId(option);
        if (!acc.some((o) => getOptionId(o) === key)) acc.push(option);
        return acc;
      }, [] as ScheduleOption[]);
    return bestOptions(unique, 3);
  }, [tomorrowData, tomorrowQueryStartTime, tomorrowQueryEndTime]);

  // Helper to get provider names from option - memoized to prevent recreating on every render
  const getProviderNames = useCallback((option: ScheduleOption): string[] => {
    const providers = data?.scheduleOption?.providers || tomorrowData?.scheduleOption?.providers || [];
    return option.resources
      .map((r) => {
        const provider = providers.find((p) => p.id === r.providerId);
        return provider ? `${provider.firstName} ${provider.lastName}` : `Provider ${r.providerId}`;
      })
      .filter(Boolean);
  }, [data?.scheduleOption?.providers]);

  // Track if we've done initial auto-select to prevent loops
  // Reset when recommendedTime changes so new filtered options get auto-selected
  const hasAutoSelected = useRef(false);
  const prevRecommendedTime = useRef(recommendedTime);
  if (prevRecommendedTime.current !== recommendedTime) {
    prevRecommendedTime.current = recommendedTime;
    hasAutoSelected.current = false;
  }

  // Auto-select best option when today options arrive (only once per recommendedTime)
  useEffect(() => {
    if (topThreeOptions.length > 0 && !selectedOption && !hasAutoSelected.current && data?.scheduleOption?.providers) {
      hasAutoSelected.current = true;
      const best = topThreeOptions[0];
      setSelectedOption(best);
      onOptionSelected({
        startTime: best.startTime,
        resources: best.resources.map(r => ({
          providerId: r.providerId,
          shiftId: r.shiftId,
          raceConditionHash: r.raceConditionHash,
        })),
        runId: best.runId,
        scores: best.scores,
        providerNames: getProviderNames(best),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topThreeOptions.length, data?.scheduleOption?.providers]);

  // Auto-select best tomorrow option when today has no options (only once per recommendedTime)
  useEffect(() => {
    if (
      topThreeOptions.length === 0 &&
      tomorrowOptions.length > 0 &&
      !selectedOption &&
      !hasAutoSelected.current &&
      tomorrowData?.scheduleOption?.providers
    ) {
      hasAutoSelected.current = true;
      const best = tomorrowOptions[0];
      setSelectedOption(best);
      onOptionSelected({
        startTime: best.startTime,
        resources: best.resources.map(r => ({
          providerId: r.providerId,
          shiftId: r.shiftId,
          raceConditionHash: r.raceConditionHash,
        })),
        runId: best.runId,
        scores: best.scores,
        providerNames: getProviderNames(best),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topThreeOptions.length, tomorrowOptions.length, tomorrowData?.scheduleOption?.providers]);

  // Handle visit type change
  const handleVisitTypeChange = (visitTypeId: number) => {
    onVisitTypeChange(visitTypeId);
    setSelectedOption(null); // Clear selection when visit type changes
    onOptionSelected(null);
  };

  // Handle option selection
  const handleOptionSelection = (option: ScheduleOption) => {
    setSelectedOption(option);
    onOptionSelected({
      startTime: option.startTime,
      resources: option.resources.map(r => ({
        providerId: r.providerId,
        shiftId: r.shiftId,
        raceConditionHash: r.raceConditionHash,
      })),
      runId: option.runId,
      scores: option.scores,
      providerNames: getProviderNames(option),
    });
  };

  // Get all available visit types
  const availableVisitTypes = Object.values(orgConfig.visitTypes);

  return (
    <Box>
      {/* Visit Type Selection */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Visit Type</InputLabel>
        <Select
          value={selectedVisitTypeId || admissionVisitType?.id || ''}
          label="Visit Type"
          onChange={(e) => handleVisitTypeChange(Number(e.target.value))}
          disabled={disabled || orgConfig.loading}
        >
          {availableVisitTypes.map((vt) => vt && (
            <MenuItem key={vt.id} value={vt.id}>
              {vt.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Loading State */}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="rectangular" height={100} />
          <Skeleton variant="text" sx={{ mt: 1 }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Error Loading Options</AlertTitle>
          {error.message || 'Failed to load visit options. Please try again.'}
        </Alert>
      )}

      {/* Options Display */}
      {!loading && !error && topThreeOptions.length > 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <RadioGroup
                name="admission-options"
                value={selectedOption ? getOptionId(selectedOption) : ''}
              >
                <Grid2 container spacing={2}>
                  {topThreeOptions.map((option, index) => {
                    const optionId = getOptionId(option);
                    const time = timeLabelWithZone(option.startTime, { ampm: true, timezone: patientTimezone });
                    const date = formatInTimeZone(new Date(option.startTime), patientTimezone, 'EEE, MMM d');

                    // Get provider names for this option
                    const providerNames = option.resources
                      .map((r) => {
                        const provider = data?.scheduleOption?.providers.find(
                          (p) => p.id === r.providerId
                        );
                        return provider ? `${provider.firstName} ${provider.lastName}` : '';
                      })
                      .filter(Boolean)
                      .join(', ');

                    // Drive time
                    const driveTime = Math.max(option.originDrive, option.destinationDrive);

                    return (
                      <Grid2 key={optionId} size={{ xs: 12, md: 4 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor: selectedOption && getOptionId(selectedOption) === optionId ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
                            bgcolor: selectedOption && getOptionId(selectedOption) === optionId ? 'primary.lighter' : 'background.paper',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'action.hover',
                            },
                            transition: 'all 0.2s',
                          }}
                          onClick={() => handleOptionSelection(option)}
                        >
                          <FormControlLabel
                            value={optionId}
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {date}
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                  {time}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {providerNames} • {driveTime}min drive
                                </Typography>
                              </Box>
                            }
                            sx={{ m: 0, width: '100%' }}
                          />
                        </Paper>
                      </Grid2>
                    );
                  })}
                </Grid2>
              </RadioGroup>
            </FormControl>
          </Box>
        </>
      )}

      {/* No Options Today — Show Tomorrow's Options */}
      {!loading && !error && topThreeOptions.length === 0 && recommendedTime && (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>No Providers Available Today</AlertTitle>
            <Typography variant="body2">
              No providers are available at {timeLabelWithZone(recommendedTime, { ampm: true, timezone: patientTimezone })}.
              Checking tomorrow's availability…
            </Typography>
          </Alert>

          {/* Tomorrow loading */}
          {tomorrowLoading && (
            <Box sx={{ mb: 2 }}>
              <Skeleton variant="rectangular" height={100} />
              <Skeleton variant="text" sx={{ mt: 1 }} />
            </Box>
          )}

          {/* Tomorrow error */}
          {tomorrowError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {tomorrowError.message || 'Failed to load tomorrow\'s options.'}
            </Alert>
          )}

          {/* Tomorrow options */}
          {!tomorrowLoading && !tomorrowError && tomorrowOptions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Best Available — Tomorrow ({formatInTimeZone(new Date(tomorrowQueryStartTime), patientTimezone, 'EEE, MMM d')})
              </Typography>
              <FormControl fullWidth>
                <RadioGroup
                  name="admission-options-tomorrow"
                  value={selectedOption ? getOptionId(selectedOption) : ''}
                >
                  <Grid2 container spacing={2}>
                    {tomorrowOptions.map((option) => {
                      const optionId = getOptionId(option);
                      const time = timeLabelWithZone(option.startTime, { ampm: true, timezone: patientTimezone });
                      const date = formatInTimeZone(new Date(option.startTime), patientTimezone, 'EEE, MMM d');
                      const providerNames = option.resources
                        .map((r) => {
                          const provider = tomorrowData?.scheduleOption?.providers.find((p) => p.id === r.providerId);
                          return provider ? `${provider.firstName} ${provider.lastName}` : '';
                        })
                        .filter(Boolean)
                        .join(', ');
                      const driveTime = Math.max(option.originDrive, option.destinationDrive);

                      return (
                        <Grid2 key={optionId} size={{ xs: 12, md: 4 }}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              border: '2px solid',
                              borderColor: selectedOption && getOptionId(selectedOption) === optionId ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
                              bgcolor: selectedOption && getOptionId(selectedOption) === optionId ? 'primary.lighter' : 'background.paper',
                              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                              transition: 'all 0.2s',
                            }}
                            onClick={() => handleOptionSelection(option)}
                          >
                            <FormControlLabel
                              value={optionId}
                              control={<Radio />}
                              label={
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {date}
                                  </Typography>
                                  <Typography variant="body1" fontWeight={500}>
                                    {time}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {providerNames} • {driveTime}min drive
                                  </Typography>
                                </Box>
                              }
                              sx={{ m: 0, width: '100%' }}
                            />
                          </Paper>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                </RadioGroup>
              </FormControl>
            </Box>
          )}

          {/* No options tomorrow either */}
          {!tomorrowLoading && !tomorrowError && tomorrowOptions.length === 0 && !skipTomorrowQuery && (
            <Alert severity="error">
              No providers available today or tomorrow. Please contact nearby providers directly to coordinate admission.
            </Alert>
          )}
        </Box>
      )}

      {/* Disabled/Waiting State */}
      {!recommendedTime && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Complete supply and transportation coordination to see available
            visit options.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}
